/**
 * Generic worker-request helper. Wraps the `postMessage` + listener +
 * Promise dance used by every main-thread caller that needs to await a
 * specific worker reply.
 *
 * The worker's `runTaskWithErrorReply` (Worker/worker.ts) replies on
 * the same `task` value as the request, with either the success payload
 * or `{ error: string }`. There is no correlation id on the wire — this
 * helper uses task-match + first-reply-wins, which is safe in
 * single-flight callers (i.e. callers that guard concurrency upstream).
 *
 * Errors arrive in two shapes and both reject the Promise:
 * - `{ error: "<message>" }` — explicit worker failure → rejects with
 *   `new Error(message)`.
 * - Unexpected payload shape — rejects with `"Worker returned an
 *   unexpected payload for <task>"`.
 *
 * For tasks that DO carry a correlation id (e.g. `SqliteSyncTable` with
 * `requestId`), pass an `isMatch` predicate to disambiguate replies
 * across concurrent requests for the same task type.
 */
import type { FromWorkerMessage, TaskType, ToWorkerMessage } from "./entities";

export interface WorkerRequestOptions {
  /** Optional reply matcher beyond task type. Used by tasks with a
   *  correlation id (e.g. `requestId` on SqliteSyncTable) so multiple
   *  concurrent requests for the same task don't cross-resolve. */
  isMatch?: (data: unknown) => boolean;
}

export async function requestFromWorker<TOut>(
  worker: Worker,
  task: TaskType,
  data: ToWorkerMessage["data"],
  options: WorkerRequestOptions = {},
): Promise<TOut> {
  const { isMatch } = options;
  return await new Promise<TOut>((resolve, reject) => {
    // One abort drops all three listeners; a reply is single-shot.
    const listening = new AbortController();
    const settle = () => listening.abort();

    const onMessage = (event: MessageEvent<FromWorkerMessage>) => {
      const reply = event.data as {
        task?: TaskType;
        data?: unknown;
      };
      if (reply.task !== task) return;
      if (isMatch && !isMatch(reply.data)) return;
      settle();
      const payload = reply.data;
      if (
        payload &&
        typeof payload === "object" &&
        "error" in payload &&
        typeof (payload as { error: unknown }).error === "string"
      ) {
        reject(new Error((payload as { error: string }).error));
        return;
      }
      if (payload === undefined || payload === null) {
        reject(new Error(`Worker returned an empty payload for ${task}`));
        return;
      }
      resolve(payload as TOut);
    };

    // A worker that throws at top level or fails to deserialize the reply
    // never sends a message, so without these the caller waits forever.
    const onFailure = (event: Event) => {
      settle();
      const detail =
        event instanceof ErrorEvent && event.message
          ? `: ${event.message}`
          : "";
      reject(new Error(`Worker failed before replying to ${task}${detail}`));
    };

    const opts = { signal: listening.signal };
    worker.addEventListener("message", onMessage, opts);
    worker.addEventListener("error", onFailure, opts);
    worker.addEventListener("messageerror", onFailure, opts);

    worker.postMessage({ task, data } as ToWorkerMessage);
  });
}
